import axios from 'axios';
import ChatGroup from '@/chat/models/ChatGroup';
import { DossierId } from '@/dossier/models/AbstractDossier';
import { WorkshopTeamId } from '@/general/models/WorkshopTeam';
import { BrandId } from '@/shared/models/Brand';
import { OutletId } from '@/shared/models/Outlet';
import User, { UserId } from '@/shared/models/User';
import { present } from '@/shared/utils/typescript';
import { formatMemoData, parseMemo, parseMemos, parseMemoTags } from '../converters/memoConverters';
import Memo, { MemoData, MemoId } from '../models/Memo';
import MemoTag, { MemoTagId } from '../models/MemoTag';
import TaskDossier from '../models/TaskDossier';

export interface MemoCriteria {
  outletIds: Array<OutletId>;
  brandId: BrandId | null;
  teamId: WorkshopTeamId | null;
  userIds: Array<UserId>;
  tagIds: Array<MemoTagId>;
}

const memoType = 1;

export default class MemoClient {
  static async loadAllForDossier(dossierId: DossierId): Promise<Array<Memo>> {
    const response = await axios.get(`/api/mobopro/v1/dossiers/${dossierId}/memos`);
    const memos = parseMemos(response.data);
    memos.forEach(m => m.dossier = TaskDossier.withId(dossierId));
    return memos;
  }

  static async loadAllTaskMemos(criteria: MemoCriteria): Promise<Array<Memo>> {
    const data = {
      moboOutletIds: criteria.outletIds,
      brandId: criteria.brandId,
      teamId: criteria.teamId,
      outletUserIds: criteria.userIds,
      tagIds: criteria.tagIds,
    };
    const response = await axios.post('/api/mobopro/v1/dossiermemos', data);
    return parseMemos(response.data);
  }

  static async loadTags(): Promise<Array<MemoTag>> {
    const response = await axios.get('/api/mobopro/v1/dossiersmemotags');
    return parseMemoTags(response.data);
  }

  static async create(data: MemoData, dossierId: DossierId): Promise<Memo> {
    const response = await axios.post(`/api/mobopro/v1/dossiers/${dossierId}/memos`, formatMemoData(data));
    const memo = present(parseMemo(response.data));

    // TODO @jan workaround until api returns resolvedById
    if (!data.resolvableBy) {
      memo.resolvableBy = null;
    } else if ('groupId' in data.resolvableBy) {
      memo.resolvableBy = ChatGroup.withId(data.resolvableBy.groupId);
    } else if ('userId' in data.resolvableBy) {
      memo.resolvableBy = User.withId(data.resolvableBy.userId);
    }

    return memo;
  }

  static async update(id: MemoId, data: MemoData, dossierId: DossierId): Promise<void> {
    await axios.patch(`/api/mobopro/v1/dossiers/${dossierId}/memos/${id}`, formatMemoData(data));
  }

  static async resolve(id: MemoId, dossierId: DossierId): Promise<void> {
    await axios.put(`/api/mobopro/v1/dossiers/${dossierId}/resolvememo`, { id, memoType });
  }

  static async remove(id: MemoId, dossierId: DossierId): Promise<void> {
    await axios.delete(`/api/mobopro/v1/dossiers/${dossierId}/memos/${id}`);
  }
}
